import { authHeader } from "./token";
import type { Envelope, PaginatedEnvelope } from "./types";

/**
 * The one place this app talks to the API.
 *
 * Three things are load-bearing and easy to get wrong:
 *
 * 1. **`credentials: "include"` on every call.** The API issues the refresh
 *    token as an httpOnly cookie on `api.bikerhat.com`. The browser attaches it
 *    automatically, but only if the request opts into credentials. The sibling
 *    GarirHat admin app sets `withCredentials: false` and stores the refresh
 *    token in `localStorage` instead — that is trap #2 in the fork audit, and it
 *    is why this file never touches `localStorage` or `sessionStorage` at all.
 *
 * 2. **Cross-subdomain cookies do work here.** `partner.bikerhat.com` and
 *    `api.bikerhat.com` are the same *site* (both under bikerhat.com), and the
 *    refresh cookie is host-only to the API, so it is sent normally. CORS grants
 *    `Access-Control-Allow-Credentials` because the origin is listed in the
 *    API's `FIRST_PARTY_ORIGINS`. **`partner.bikerhat.com` must be in that env
 *    var or every mutation 403s at the CORS layer** — a deployment setting, not
 *    a code change.
 *
 * 3. **The envelope.** Success is `{ success, message, data }`; paginated
 *    responses also carry `total/page/limit/totalPages` at the top level. Errors
 *    are `{ success: false, message, details? }` — and a 422 publish refusal puts
 *    its list at `details.blocking`, not at the top level.
 */

const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, "");

if (!BASE) {
  throw new Error(
    "VITE_API_BASE is not set. Copy .env.example to .env and point it at the API, e.g. https://api.bikerhat.com",
  );
}

export class ApiError extends Error {
  // Explicit fields, not constructor parameter properties: the tsconfig sets
  // `erasableSyntaxOnly`, which forbids syntax that is not plain JS.
  readonly status: number;
  readonly details?: unknown;
  /** Machine-readable discriminator, e.g. "PHONE_UNVERIFIED". */
  readonly code?: string;

  constructor(status: number, message: string, details?: unknown, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
    this.code = code;
  }

  /** The phone still needs confirming, so sign-in needs a verification step. */
  get isPhoneUnverified(): boolean {
    return this.code === "PHONE_UNVERIFIED";
  }

  /** A 401 means the access token expired; the auth provider refreshes on it. */
  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  /**
   * The blocking reasons from a 422, if this is one. Read from `details.blocking`
   * because that is where the exception filter puts them — a list at the top
   * level is simply absent.
   */
  get blocking(): string[] {
    const d = this.details as { blocking?: unknown } | undefined;
    return Array.isArray(d?.blocking) ? (d.blocking as string[]) : [];
  }
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

async function request<T>(
  method: Method,
  path: string,
  options: { body?: unknown; signal?: AbortSignal } = {},
): Promise<{ data: T; envelope: Envelope<T> }> {
  const bearer = authHeader();
  const res = await fetch(`${BASE}/api/v1${path}`, {
    method,
    // Load-bearing; see note 1 above.
    credentials: "include",
    headers: {
      ...(bearer ?? {}),
      ...(options.body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = { message: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    const body = json as { message?: string; details?: unknown; code?: string } | null;
    throw new ApiError(
      res.status,
      body?.message ?? `Request failed (${res.status})`,
      body?.details,
      body?.code,
    );
  }

  const envelope = json as Envelope<T>;
  return { data: envelope.data, envelope };
}

/** A request whose `data` is a single object. */
export function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>("GET", path, { signal }).then((r) => r.data);
}

/** A request whose `data` is an array (no envelope lifting needed). */
export function getList<T>(path: string, signal?: AbortSignal): Promise<T[]> {
  return request<T[]>("GET", path, { signal }).then((r) => r.data);
}

/**
 * A paginated request. The interceptor lifts `total/page/limit/totalPages` to
 * the top level only when the payload carries all four alongside `data`, so this
 * reads them from the envelope rather than from inside `data`.
 */
export async function getPaginated<T>(
  path: string,
  signal?: AbortSignal,
): Promise<{ rows: T[]; total: number; page: number; totalPages: number }> {
  const res = await fetch(`${BASE}/api/v1${path}`, {
    credentials: "include",
    headers: { ...(authHeader() ?? {}) },
    signal,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      (json as { message?: string })?.message ?? `Request failed (${res.status})`,
      (json as { details?: unknown })?.details,
    );
  }

  const page = json as PaginatedEnvelope<T>;
  return {
    rows: page.data ?? [],
    total: page.total ?? 0,
    page: page.page ?? 1,
    totalPages: page.totalPages ?? 0,
  };
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, { body }).then((r) => r.data);
}

/** Multipart upload: deliberately no `Content-Type` header so the browser sets
 *  the multipart boundary itself; the bearer token still goes via authHeader. */
export async function postForm<T>(
  path: string,
  formData: FormData,
): Promise<{ data: T; envelope: Envelope<T> }> {
  const bearer = authHeader();
  const res = await fetch(`${BASE}/api/v1${path}`, {
    method: "POST",
    credentials: "include",
    headers: { ...(bearer ?? {}) },
    body: formData,
  });

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = { message: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    const body = json as { message?: string; details?: unknown; code?: string } | null;
    throw new ApiError(
      res.status,
      body?.message ?? `Request failed (${res.status})`,
      body?.details,
      body?.code,
    );
  }

  const envelope = json as Envelope<T>;
  return { data: envelope.data, envelope };
}

export function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>("PATCH", path, { body }).then((r) => r.data);
}

export function del<T>(path: string): Promise<T> {
  return request<T>("DELETE", path).then((r) => r.data);
}

export { BASE as API_BASE };